import { useState } from 'react';
import { Wine } from '../types';
import { getWineDisplayName, hasDistinctName } from '../utils';

interface WineDetailProps {
  wine: Wine;
  isRefreshing?: boolean;
  onEdit: () => void;
  onDrink: () => void;
  onAddScore: () => void;
  onUpdateQuantity: (delta: number) => void;
  onRefreshRatings: () => void;
  onSaveUserRating: (rating: number) => Promise<void>;
  onClose: () => void;
}

export function WineDetail({ wine, isRefreshing, onEdit, onDrink, onAddScore, onUpdateQuantity, onRefreshRatings, onSaveUserRating, onClose }: WineDetailProps) {
  const [editingRating, setEditingRating] = useState(false);
  const [ratingInput, setRatingInput] = useState(wine.user_rating?.toString() || '');
  const [savingRating, setSavingRating] = useState(false);
  
  const vivinoScore = wine.critic_scores?.find(s => s.critic_name === 'Vivino');
  const otherScores = wine.critic_scores?.filter(s => s.critic_name !== 'Vivino') || [];
  const displayName = getWineDisplayName(wine);
  const showProducerLine = hasDistinctName(wine);

  const formatLastUpdated = (dateStr?: string) => {
    if (!dateStr) return null;
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  // Wine color indicator
  const colorMap: Record<string, string> = {
    red: '#722f37',
    white: '#f5e6a3',
    rose: '#e8b4b8',
    sparkling: '#f0e68c',
    dessert: '#d4a574',
    fortified: '#8b4513'
  };

  const handleSaveRating = async () => {
    const rating = parseFloat(ratingInput);
    if (isNaN(rating) || rating < 0 || rating > 100) {
      alert('Please enter a rating between 0 and 100');
      return;
    }
    
    setSavingRating(true);
    try {
      await onSaveUserRating(rating);
      setEditingRating(false);
    } catch (error) {
      console.error('Error saving rating:', error);
      alert('Error saving rating');
    } finally {
      setSavingRating(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div 
        className="ios-sheet w-full"
        style={{ height: '90vh', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}
        onClick={e => e.stopPropagation()}
      >
        <div className="ios-sheet-handle" />

        {/* Header */}
        <div style={{ 
          padding: '0 20px 16px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0
        }}>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-wine)',
              fontSize: '16px',
              cursor: 'pointer'
            }}
          >
            ← Back
          </button>
          <h2 className="wine-title" style={{ 
            fontSize: '18px', 
            margin: 0,
            color: 'var(--color-text)'
          }}>
            Wine Details
          </h2>
          <button
            onClick={onEdit}
            style={{
              background: 'none',
              border: 'none',
              color: 'var(--color-wine)',
              fontSize: '16px',
              cursor: 'pointer'
            }}
          >
            Edit
          </button>
        </div>

        {/* Scrollable Content */}
        <div style={{ 
          flex: 1, 
          overflowY: 'auto', 
          padding: '0 16px',
          paddingBottom: '100px'
        }}>
          {/* Wine Title Card */}
          <div className="ios-card" style={{ padding: '20px', marginBottom: '12px', textAlign: 'center' }}>
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              gap: '8px',
              marginBottom: '8px'
            }}>
              <span style={{
                width: '12px',
                height: '12px',
                borderRadius: '6px',
                background: colorMap[wine.color] || '#722f37',
                border: wine.color === 'white' ? '1px solid #ccc' : 'none'
              }} />
              <span style={{ fontSize: '13px', color: 'var(--color-text-muted)', textTransform: 'capitalize' }}>
                {wine.color} {wine.wine_type !== 'still' && wine.wine_type}
              </span>
            </div>
            <h1 className="wine-title" style={{ 
              fontSize: '22px', 
              color: 'var(--color-wine)',
              marginBottom: '4px'
            }}>
              {displayName}
            </h1>
            {wine.vintage && (
              <p style={{ fontSize: '18px', color: 'var(--color-text)', marginBottom: '4px' }}>{wine.vintage}</p>
            )}
            {showProducerLine && (
              <p style={{ fontSize: '15px', color: 'var(--color-text-muted)' }}>{wine.producer}</p>
            )}
          </div>

          {/* Quick Stats */}
          <div className="ios-card" style={{ overflow: 'hidden', marginBottom: '12px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr' }}>
              <div style={{ 
                padding: '16px', 
                textAlign: 'center',
                borderRight: '1px solid var(--color-border)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                  <button
                    onClick={() => onUpdateQuantity(-1)}
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '14px',
                      border: 'none',
                      background: 'var(--color-parchment-dark)',
                      color: 'var(--color-text)',
                      fontSize: '18px',
                      cursor: 'pointer'
                    }}
                  >
                    −
                  </button>
                  <span style={{ fontSize: '24px', fontWeight: '600', color: 'var(--color-wine)', minWidth: '32px' }}>
                    {wine.quantity}
                  </span>
                  <button
                    onClick={() => onUpdateQuantity(1)}
                    style={{
                      width: '28px',
                      height: '28px',
                      borderRadius: '14px',
                      border: 'none',
                      background: 'var(--color-parchment-dark)',
                      color: 'var(--color-text)',
                      fontSize: '18px',
                      cursor: 'pointer'
                    }}
                  >
                    +
                  </button>
                </div>
                <p className="ios-label" style={{ marginTop: '4px' }}>Bottles</p>
              </div>
              <div style={{ 
                padding: '16px', 
                textAlign: 'center',
                borderRight: '1px solid var(--color-border)'
              }}>
                <p style={{ fontSize: '24px', fontWeight: '600', color: 'var(--color-wine)' }}>
                  {wine.purchase_price ? `$${wine.purchase_price}` : '—'}
                </p>
                <p className="ios-label" style={{ marginTop: '4px' }}>Price</p>
              </div>
              <div 
                style={{ padding: '16px', textAlign: 'center', cursor: 'pointer' }}
                onClick={() => {
                  if (!editingRating && wine.canonical_wine_id) {
                    setRatingInput(wine.user_rating?.toString() || '');
                    setEditingRating(true);
                  }
                }}
              >
                {editingRating ? (
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                    <input
                      type="number"
                      value={ratingInput}
                      onChange={(e) => setRatingInput(e.target.value)}
                      min="0"
                      max="100"
                      style={{
                        width: '50px',
                        padding: '4px',
                        fontSize: '18px',
                        fontWeight: '600',
                        textAlign: 'center',
                        border: '1px solid var(--color-wine)',
                        borderRadius: '4px',
                        color: 'var(--color-wine)'
                      }}
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSaveRating();
                        if (e.key === 'Escape') setEditingRating(false);
                      }}
                    />
                    <button
                      onClick={(e) => { e.stopPropagation(); handleSaveRating(); }}
                      disabled={savingRating}
                      style={{
                        background: 'var(--color-wine)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        padding: '4px 8px',
                        fontSize: '12px',
                        cursor: 'pointer'
                      }}
                    >
                      {savingRating ? '...' : '✓'}
                    </button>
                  </div>
                ) : (
                  <p style={{ fontSize: '24px', fontWeight: '600', color: 'var(--color-wine)' }}>
                    {wine.user_rating || '—'}
                  </p>
                )}
                <p className="ios-label" style={{ marginTop: '4px' }}>
                  Your Rating {!editingRating && wine.canonical_wine_id && <span style={{ fontSize: '10px' }}>✎</span>}
                </p>
              </div>
            </div>
            
            {/* CellarLog Score Row */}
            {wine.cellarlog_score && (
              <div style={{ 
                padding: '12px 16px', 
                borderTop: '1px solid var(--color-border)',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                gap: '8px'
              }}>
                <span style={{ fontSize: '16px' }}>🍷</span>
                <span style={{ fontSize: '15px', color: 'var(--color-text)' }}>
                  <strong>CellarLog Score:</strong> {wine.cellarlog_score}
                </span>
                <span style={{ fontSize: '13px', color: 'var(--color-text-muted)' }}>
                  ({wine.cellarlog_rating_count} {wine.cellarlog_rating_count === 1 ? 'rating' : 'ratings'})
                </span>
              </div>
            )}
          </div>

          {/* Ratings Section */}
          <div className="ios-card" style={{ padding: '16px', marginBottom: '12px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
              <div>
                <p className="ios-label" style={{ marginBottom: '2px' }}>Ratings</p>
                {wine.ratings_updated_at && (
                  <p style={{ fontSize: '11px', color: 'var(--color-text-muted)' }}>
                    Updated {formatLastUpdated(wine.ratings_updated_at)}
                  </p>
                )}
              </div>
              <div style={{ display: 'flex', gap: '8px' }}>
                <button
                  onClick={onRefreshRatings}
                  disabled={isRefreshing}
                  className="ios-btn ios-btn-secondary"
                  style={{ 
                    padding: '6px 12px', 
                    fontSize: '14px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    minWidth: isRefreshing ? '110px' : 'auto'
                  }}
                >
                  {isRefreshing ? (
                    <>
                      <span className="animate-spin" style={{ fontSize: '14px' }}>🍷</span>
                      <span>Fetching...</span>
                    </>
                  ) : '🔄'}
                </button>
                <button
                  onClick={onAddScore}
                  className="ios-btn ios-btn-primary"
                  style={{ padding: '6px 12px', fontSize: '14px' }}
                >
                  + Add
                </button>
              </div>
            </div>

            {/* Refreshing indicator */}
            {isRefreshing && (
              <div className="animate-fade-in" style={{ 
                padding: '12px', 
                marginBottom: '12px',
                background: 'var(--color-parchment)',
                borderRadius: '8px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div className="animate-pulse" style={{ 
                  width: '40px', 
                  height: '40px', 
                  borderRadius: '8px',
                  background: 'var(--color-wine)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '20px'
                }}>
                  🍷
                </div>
                <div style={{ flex: 1 }}>
                  <p style={{ fontSize: '14px', color: 'var(--color-text)', marginBottom: '2px' }}>
                    Searching for ratings...
                  </p>
                  <p style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
                    Checking Vivino, Wine Spectator & more
                  </p>
                </div>
              </div>
            )}

            {(vivinoScore || otherScores.length > 0) ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {vivinoScore && (
                  <div style={{ 
                    display: 'flex', 
                    justifyContent: 'space-between', 
                    alignItems: 'center',
                    padding: '12px',
                    background: 'var(--color-parchment)',
                    borderRadius: '8px'
                  }}>
                    <span style={{ color: 'var(--color-text)' }}>Vivino</span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <span style={{ fontSize: '18px', fontWeight: '600', color: 'var(--color-wine)' }}>
                        {vivinoScore.score.toFixed(1)}
                      </span>
                      <span style={{ color: 'var(--color-gold)' }}>★</span>
                    </div>
                  </div>
                )}
                {otherScores.map(score => (
                  <div 
                    key={score.id} 
                    style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      padding: '12px',
                      background: 'var(--color-parchment)',
                      borderRadius: '8px'
                    }}
                  >
                    <span style={{ color: 'var(--color-text)' }}>{score.critic_name}</span>
                    <span style={{ fontSize: '18px', fontWeight: '600', color: 'var(--color-wine)' }}>
                      {score.score}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p style={{ fontSize: '14px', color: 'var(--color-text-muted)' }}>
                No ratings yet. Tap 🔄 to fetch from the web.
              </p>
            )}
          </div>

          {/* Critic Notes */}
          <div className="ios-card" style={{ padding: '16px', marginBottom: '12px' }}>
            <p className="ios-label" style={{ marginBottom: '8px' }}>Critic Notes</p>
            {wine.critic_notes ? (
              <p style={{ fontSize: '14px', fontStyle: 'italic', color: 'var(--color-text)', lineHeight: '1.5' }}>
                "{wine.critic_notes}"
              </p>
            ) : (
              <p style={{ fontSize: '14px', color: 'var(--color-text-muted)' }}>
                No critic notes yet. Tap 🔄 above to fetch.
              </p>
            )}
          </div>

          {/* Wine Details */}
          <div className="ios-card" style={{ overflow: 'hidden', marginBottom: '12px' }}>
            <p className="ios-label" style={{ padding: '12px 16px 8px' }}>Details</p>
            
            {wine.varietal && (
              <div className="ios-list-row" style={{ borderBottom: '1px solid var(--color-border)' }}>
                <span style={{ color: 'var(--color-text-muted)' }}>Varietal</span>
                <span style={{ color: 'var(--color-text)' }}>{wine.varietal}</span>
              </div>
            )}
            {wine.region && (
              <div className="ios-list-row" style={{ borderBottom: '1px solid var(--color-border)' }}>
                <span style={{ color: 'var(--color-text-muted)' }}>Region</span>
                <span style={{ color: 'var(--color-text)' }}>{wine.region}</span>
              </div>
            )}
            {wine.appellation && (
              <div className="ios-list-row" style={{ borderBottom: '1px solid var(--color-border)' }}>
                <span style={{ color: 'var(--color-text-muted)' }}>Appellation</span>
                <span style={{ color: 'var(--color-text)' }}>{wine.appellation}</span>
              </div>
            )}
            {wine.country && (
              <div className="ios-list-row" style={{ borderBottom: '1px solid var(--color-border)' }}>
                <span style={{ color: 'var(--color-text-muted)' }}>Country</span>
                <span style={{ color: 'var(--color-text)' }}>{wine.country}</span>
              </div>
            )}
            {wine.cellar_location && (
              <div className="ios-list-row" style={{ borderBottom: '1px solid var(--color-border)' }}>
                <span style={{ color: 'var(--color-text-muted)' }}>Location</span>
                <span style={{ color: 'var(--color-text)' }}>{wine.cellar_location}</span>
              </div>
            )}
            {wine.bottle_size && wine.bottle_size !== '750ml' && (
              <div className="ios-list-row">
                <span style={{ color: 'var(--color-text-muted)' }}>Bottle Size</span>
                <span style={{ color: 'var(--color-text)' }}>{wine.bottle_size}</span>
              </div>
            )}
          </div>

          {/* Personal Notes */}
          {wine.tasting_notes && (
            <div className="ios-card" style={{ padding: '16px', marginBottom: '12px' }}>
              <p className="ios-label" style={{ marginBottom: '8px' }}>My Notes</p>
              <p style={{ fontSize: '14px', color: 'var(--color-text)', lineHeight: '1.5' }}>
                {wine.tasting_notes}
              </p>
            </div>
          )}
        </div>

        {/* Fixed Bottom Action */}
        <div style={{ 
          padding: '16px 20px',
          paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
          background: 'var(--color-card)',
          borderTop: '1px solid var(--color-border)',
          flexShrink: 0
        }}>
          <button
            onClick={onDrink}
            className="ios-btn ios-btn-primary w-full"
            style={{ padding: '14px' }}
          >
            🍷 Drink a Bottle
          </button>
        </div>
      </div>
    </div>
  );
}
